'use client'
import { useRef, useState } from 'react';

import { useClickOutside } from '@/hooks/useClickOutside';
import { cn } from '@/lib/utils';

import Button from '@/components/atoms/button';
import { Dropdown, DropdownMenu } from '@/components/atoms/dropdown';
import LinkButton from '@/components/atoms/linkButton';
import { Text } from '@/components/atoms/text';

import { buildShareLinks } from './buildShareLinks';

import { useTranslation } from '@/lib/i18n/i18n.client';
import IconDone from '~/svg/IconDone.svg';
import IconFacebook from '~/svg/IconFacebook.svg';
import IconLink from '~/svg/IconLink.svg';
import IconLinkedIn from '~/svg/IconLinkedIn.svg';
import IconShare from '~/svg/IconShare.svg';
import IconTwitter from '~/svg/IconTwitter.svg';

interface PostShareMenuProps {
  url: string;
  title?: string;
  excerpt?: React.ReactNode;
}

const PostShareMenu = ({ url, title, excerpt }: PostShareMenuProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [showingCopyMessage, setShowingCopyMessage] = useState(false);

  const shareLinks = buildShareLinks({ url, title: title ?? '' });

  const [isClicked, setIsClicked] = useState(false);
  const { texts } = useTranslation();

  const copyLink = async () => {
    if (showingCopyMessage) return;

    setShowingCopyMessage(true);
    setIsClicked(true);

    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsOpen(false);
        setIsClicked(false);
        setShowingCopyMessage(false);
      }, 1500);
    }
  };
  const ShareMenuref = useRef<HTMLDivElement>(null);

  useClickOutside({
    ref: ShareMenuref as React.MutableRefObject<HTMLElement>,
    callback: () => {
      setIsOpen(false);
    },
  });

  const socialShareOptions = [
    {
      Icon: IconLinkedIn,
      label: 'LinkedIn',
      url: shareLinks.linkedin,
    },
    {
      Icon: IconTwitter,
      label: 'Twitter',
      url: shareLinks.twitter,
    },
    {
      Icon: IconFacebook,
      label: 'Facebook',
      url: shareLinks.facebook,
    },
  ];

  return (
    <div data-tracker-component='share-menu'>
      <Button
        variant='text'
        className='text-neutral-N99 hover:bg-neutral-N04 flex h-8 w-8 justify-center rounded-full'
        onClick={() => setIsOpen(!isOpen)}
      >
        <IconShare />
      </Button>

      <Dropdown className={cn(isOpen ? 'block' : 'collapse')}>
        <DropdownMenu
          align='right'
          ref={ShareMenuref}
          classNames={{
            wrapper: 'relative flex flex-col m-0 p-0',
          }}
        >
          <div
            className='bg-neutral-N00 border-neutral-N20 absolute right-0 top-0 rounded-md border shadow'
            data-tracker-component='social-share'
          >
            {socialShareOptions.map(({ Icon, label, url }) => (
              <LinkButton
                key={label}
                variant='text'
                color='secondary'
                underline={false}
                href={url}
                leftSection={<Icon />}
                classNames={{
                  root: 'px-3 h-8 hover:bg-neutral-N04 w-full justify-start rounded-none',
                  leftSection: 'text-neutral-N40',
                }}
              >
                <Text>{label}</Text>
              </LinkButton>
            ))}
            <Button
              variant='text'
              color='secondary'
              onClick={copyLink}
              leftSection={showingCopyMessage ? <IconDone /> : <IconLink />}
              classNames={{
                root: `z-10 px-3 h-8 justify-start w-full rounded-b-md font-sans
${isClicked ? '' : 'hover:bg-neutral-N04'}
                `,
                leftSection: isClicked
                  ? 'z-10 text-success-S99'
                  : 'z-10 text-neutral-N40',
              }}
            >
              <Text
                classNames={{
                  wrapper: `z-10 ${isClicked ? 'text-success-S99' : 'text-neutral-N99'
                    }`,
                }}
              >
                {showingCopyMessage ? texts.blog.copied : texts.blog.copy_link}
              </Text>
              <span
                className={`bg-success-S06 absolute bottom-0 left-0 h-8 w-full origin-left rounded-b-md transition-transform duration-1000 ease-linear 
                ${isClicked
                    ? `scale-x-(${Number(showingCopyMessage)})`
                    : 'scale-x-0'
                  }
                `}
              />
            </Button>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default PostShareMenu;
